'use client';

import Script from 'next/script';
import { usePaypalSdk } from '../../../hooks/paypal-express';
import { useCartErrorStore } from 'stores/cart';
import { PAYPAL_EXPRESS_SDK, CART_DIALOG_TYPE } from '../../../constants';
import { PayPalSmartButton } from './PaypalExpressSmartButton';
import type { PaypalExpressClientProps } from './types';
import styles from './PaypalExpressClient.module.css';

/**
 * PaypalExpressClient - Shared Client Component
 *
 * Handles PayPal SDK bootstrapping, SmartButton rendering, and error handling.
 * Used in both mobile (CartOverviewSummaryCta) and desktop (CartStepFooter) contexts.
 *
 * Error handling via Zustand:
 * - Errors are published to cartErrorStore
 * - Parent component (CartOverview) subscribes and renders error dialogs
 * - This keeps CartOverviewSummaryCta and CartStepFooter as Server Components
 *
 * Returns null when hasPaypalExpress is false or totalGross is 0 — the PayPal SDK
 * (~100KB+) must not load for users without PayPal Express or with a zero-total order.
 */
export function PaypalExpressClient({
  hasPaypalExpress,
  totalGross,
  routes,
  tagline,
  locale,
}: PaypalExpressClientProps) {
  const shouldLoadSdk = hasPaypalExpress && totalGross > 0;
  const [isPaypalSdkReady, onSdkLoad] = usePaypalSdk(shouldLoadSdk);
  const setError = useCartErrorStore(state => state.setError);

  if (!shouldLoadSdk) {
    return null;
  }

  return (
    <div className={styles['wrapper']} aria-busy={!isPaypalSdkReady}>
      <Script src={PAYPAL_EXPRESS_SDK} strategy="afterInteractive" onLoad={onSdkLoad} />

      {isPaypalSdkReady && (
        <PayPalSmartButton
          routes={routes}
          tagline={tagline}
          locale={locale}
          onGeneralError={() => setError(CART_DIALOG_TYPE.GENERAL_ERROR)}
          onShippingAddressError={() => setError(CART_DIALOG_TYPE.PAYPAL_INVALID_SHIPPING_ADDRESS)}
        />
      )}
    </div>
  );
}
